import { useEffect, useState, useMemo, useCallback } from 'react';
import Button from '@mui/material/Button';
import { Link, useSearchParams } from 'react-router-dom';
import Grid from '@mui/material/Unstable_Grid2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faSpinner, faXmark } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import Request from '../helper/Request';

import '../style/Confirm.css';
import confirmEmail from '../assets/compressed/confirm.svg';

export function Confirm() {
  const { t } = useTranslation();

  const [searchParams] = useSearchParams();

  const [counter, setCounter] = useState(30);
  const [pending, setPending] = useState(true);
  const [success, setSuccess] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [errors, setErrors] = useState({});

  const baseUrl = process.env.REACT_APP_APP_PATH;

  const emailEncoded = searchParams.get("email");
  const id = searchParams.get("id");

  const confirmAccount = useCallback(async () => {
    setPending(true);
    try {
      const response = await Request.get(`confirmAccount?email=${emailEncoded}&id=${id}`);
      setSuccess(true);
      setConfirmed(response.status === 200);
    } catch (error) {
      setSuccess(false);
      setErrors(error);
    } finally {
      setPending(false);
    }
  }, [emailEncoded, id]);

  useEffect(() => {
    confirmAccount();

    // Timer logic
    if (success && !confirmed) {
      const timer = counter > 0 && setInterval(() => setCounter((prev) => prev - 1), 1000);
      return () => clearInterval(timer);
    }
  }, [confirmAccount, success, confirmed, counter]);

  const renderGrid = (dynamicContent) => (
    <Grid component="section" justifyContent='center' xs={12} sm={12} md={12} lg={12} className="right-side submitted-form">
      <div className="form-body">
        <p>{confirmed ? t('confirmed_subtitle') : t('confirm_subtitle')}</p>
        <img id="confirm-email" className="no-drag form-image" loading="lazy" src={confirmEmail} alt="Email confirmation illustration" />
        {dynamicContent}
      </div>
    </Grid>
  );

  const renderSuccess = useMemo(() => {
    const isPending = counter > 0 && !confirmed;
    return (
      <div className='success-wrapper'>
        <div className={`spinner-wrapper ${isPending ? 'pending' : 'success'}`}>
          <FontAwesomeIcon icon={isPending ? faSpinner : faCheck} className={isPending ? 'fa-spin' : ''} aria-label="Evaluating Icon" />
        </div>
        <p>{isPending ? t('confirm_success_desc_1', { seconds: counter }) : t('confirm_success_desc_2')}</p>
        <Button
          id="signin-button"
          variant="contained"
          color="success"
          component={Link}
          to={`${baseUrl}/auth/login?l=${i18n.language}&un=${emailEncoded}`}
          target="_blank"
          disabled={isPending}
        >
          {t('sign_in')}
        </Button>
        <p className="reminder">{isPending ? t('confirm_desc_2') : ''}</p>
      </div>
    );
  }, [counter, confirmed, baseUrl, emailEncoded, t]);

  const renderError = useMemo(() => (
    <>
      <div className="spinner-wrapper error">
        <FontAwesomeIcon icon={faXmark} aria-label="Error Icon" />
      </div>
      <p>{t('confirm_error_desc_1')}</p>
      <p>{errors.response?.data?.data?.error}</p>
    </>
  ), [errors, t]);

  const renderPending = useMemo(() => (
    <>
      <div className="spinner-wrapper pending">
        <FontAwesomeIcon icon={faSpinner} className="fa-spin" aria-label="spinner icon" />
      </div>
      <p>{t('confirm_desc_1')}</p>
      <p className="reminder">{t('confirm_desc_2')}</p>
    </>
  ), [t]);

  if (!pending) {
    return renderGrid(
      success ? renderSuccess : renderError
    );
  } else {
    return renderGrid(
      renderPending
    );
  }
}
